import React, { useCallback, useRef, useState, useSyncExternalStore } from "react";

/**
 * Shared position of the floating launcher dock — the Call and Chat FABs.
 *
 * The two widgets are mounted in different subtrees of app.tsx, so instead of
 * one container they share this tiny external store and each renders its own
 * fixed container at a "slot" along the same edge (call = slot 0 on the outer
 * edge, chat = slot 1 next to it). Dragging either FAB moves the whole dock,
 * so the two can never overlap each other.
 *
 * The dock snaps to the nearest left/right edge and keeps a free vertical
 * offset, stored edge-relative (not as raw x/y) so it survives window resizes
 * and different monitors without ending up off-screen. When the dock sits in
 * the upper half of the viewport it anchors from the top and its popups open
 * downward instead of upward.
 */

export type DockSide = "left" | "right";

type DockPos = { side: DockSide; bottom: number };

const STORAGE_KEY = "FR_LAUNCHER_DOCK";
const DEFAULT_POS: DockPos = { side: "right", bottom: 24 };

export const DOCK_EDGE = 24;
export const DOCK_FAB_SIZE = 52;
const DOCK_SLOT_GAP = 16;
const DOCK_MIN_INSET = 8;
const DRAG_THRESHOLD = 5;

function readPos(): DockPos {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const v = JSON.parse(raw);
      if ((v?.side === "left" || v?.side === "right") && typeof v?.bottom === "number") return v;
    }
  } catch {
    // ignore
  }
  return DEFAULT_POS;
}

function writePos(pos: DockPos) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

// ---- external store ----
let state: { pos: DockPos; viewportH: number; dragging: boolean } = {
  pos: readPos(),
  viewportH: window.innerHeight,
  dragging: false,
};
const listeners = new Set<() => void>();

function setState(patch: Partial<typeof state>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function onResize() {
  setState({ viewportH: window.innerHeight });
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) window.addEventListener("resize", onResize);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("resize", onResize);
  };
}

const getSnapshot = () => state;

function clampBottom(bottom: number, viewportH: number) {
  return Math.max(DOCK_MIN_INSET, Math.min(viewportH - DOCK_FAB_SIZE - DOCK_MIN_INSET, bottom));
}

/** Offset from the docked edge for a given slot (0 = outermost). */
export function dockSlotInset(slot: number) {
  return DOCK_EDGE + slot * (DOCK_FAB_SIZE + DOCK_SLOT_GAP);
}

export function useLauncherDock(slot: number) {
  const { pos, viewportH, dragging } = useSyncExternalStore(subscribe, getSnapshot);
  const [isDraggingThis, setIsDraggingThis] = useState(false);
  const suppressClickRef = useRef(false);

  const bottom = clampBottom(pos.bottom, viewportH);
  const top = viewportH - bottom - DOCK_FAB_SIZE;
  // Upper half → anchor from the top so popups open downward and stay on screen.
  const anchorTop = bottom + DOCK_FAB_SIZE / 2 > viewportH / 2;
  const side = pos.side;

  const containerStyle: React.CSSProperties = {
    left: side === "left" ? dockSlotInset(slot) : "auto",
    right: side === "right" ? dockSlotInset(slot) : "auto",
    top: anchorTop ? top : "auto",
    bottom: anchorTop ? "auto" : bottom,
    flexDirection: anchorTop ? "column-reverse" : "column",
    alignItems: side === "left" ? "flex-start" : "flex-end",
  };

  /**
   * Fixed-position style for a popup that sits beside the dock (e.g. the
   * call widget's caller-info / notes columns), `inset` px from the docked
   * edge and vertically aligned with the dock.
   */
  const besideStyle = useCallback(
    (inset: number): React.CSSProperties => ({
      position: "fixed",
      [side]: inset,
      ...(anchorTop ? { top } : { bottom }),
    }),
    [side, anchorTop, top, bottom],
  );

  /** Spread onto the FAB: a press that moves past the threshold drags the dock instead of clicking. */
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;

    const onMove = (ev: PointerEvent) => {
      if (!moved) {
        if (Math.abs(ev.clientX - startX) < DRAG_THRESHOLD && Math.abs(ev.clientY - startY) < DRAG_THRESHOLD) return;
        moved = true;
        setIsDraggingThis(true);
        setState({ dragging: true });
      }
      setState({
        pos: {
          side: ev.clientX < window.innerWidth / 2 ? "left" : "right",
          bottom: clampBottom(window.innerHeight - ev.clientY - DOCK_FAB_SIZE / 2, window.innerHeight),
        },
      });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      if (!moved) return;
      setIsDraggingThis(false);
      setState({ dragging: false });
      writePos(state.pos);
      // The click that follows this pointerup must not toggle the widget.
      // Reset on the next tick in case no click fires (pointer left the FAB).
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (suppressClickRef.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  return {
    side,
    anchorTop,
    /** True while any dock FAB is being dragged — hide tooltips/popovers meanwhile. */
    dragging,
    /** True while this FAB is the one being dragged. */
    isDraggingThis,
    containerStyle,
    besideStyle,
    fabProps: { onPointerDown, onClickCapture },
  };
}
